import {
  CheckGroupProps,
  isCheckGroupProps,
} from 'components/elements/form/CheckGroup'
import {
  DivideCarFourGroupProps,
  isDivideCarFourGroupProps,
} from 'components/elements/form/DamagedPartGroup/DivideCarFourGroup'
import {
  isRadioGroupProps,
  RadioGroupProps,
} from 'components/elements/form/RadioGroup'
import {
  isSwitchGroupProps,
  SwitchGroupProps,
} from 'components/elements/form/SwitchGroup'
import {
  isTextAreaGroupProps,
  TextAreaGroupProps,
} from 'components/elements/form/TextAreaGroup'
import { ImagesAPIPayloadProps, RequestAPIPayloadProps } from 'constants/api'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import {
  OptionResource,
  RA_LCAR_COST_SC_WISHES,
  RA_LODGING_SC_WISHES,
  RA_TRANSFER_SC_WISHES,
  RESERVATION_DATE_API_FORMAT,
  VehicleStateFormItem,
  VehicleStatePartItem,
  VehicleStateSelectItem,
  VehicleStateSingleItem,
  VEHICLE_HEIGHT_MODIFICATION_CONFIRM,
  VEHICLE_STATE,
  VEHICLE_STATE_FORM,
} from 'constants/qnState'
import dayjs from 'dayjs'
import {
  AccidentImage,
  DestinationTypes,
  EngineTroubleStatusTypes,
  FallingWheelStatusTypes,
  FieldTypes,
  FourWheeledVehiclePart,
  FuelTypes,
  MotorbikePart,
  QnStateProps,
  SpareTireStatusTypes,
  TroubleSupportTypes,
} from 'states/QnState'
import {
  deleteReturnTabString,
  getSelectedCheckLabel,
  getSelectedDamagedPartLabel,
  getSelectedRadioLabel,
  isSelectedCheck,
} from 'utils/commonUtils'
import {
  hasRaLcarCost,
  hasRaLodgingTransfer,
  hasSpecialContract,
} from 'utils/specialContract'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import {
  AllPart,
  OthersStatusTypes,
  LossKeyStatusTypes,
} from '../states/QnState'

dayjs.extend(advancedFormat)

/**
 * 自走できない場合の運び先候補が処理対象ならtrue, それ以外ならfalseを返却する
 * @param state QNState
 */
export const isDestinationTarget = (state: QnStateProps): boolean => {
  const { vehicleState, vehicleStateInfo } = state

  switch (vehicleState) {
    // バッテリー・エンジン関連
    case VEHICLE_STATE.ENGINE_TROUBLE:
      switch (vehicleStateInfo.engineTroubleStatus) {
        // バッテリー上がり
        case EngineTroubleStatusTypes.battery:
          return false
        // エンジンがかからない
        case EngineTroubleStatusTypes.engineNotStart:
          // レッカーの場合は表示
          return vehicleStateInfo.troubleSupport.includes(
            TroubleSupportTypes.wrecker
          )
        // 上記以外は表示
        default:
          return true
      }
    // パンク
    case VEHICLE_STATE.FLAT_TIRE:
      // 「スペアタイヤあり」以外選択時は自走できない場合の運び先候補表示対象
      return !vehicleStateInfo.spareTireStatus.includes(
        SpareTireStatusTypes.yes
      )
    case VEHICLE_STATE.ACCIDENT: // 事故
    case VEHICLE_STATE.FALLING_WHEEL: // 落輪・乗り上げ
      return true
    // 燃料切れ
    case VEHICLE_STATE.EMPTY_FUEL:
      // 電気・LPGのみ
      return vehicleStateInfo.fuelType.includes(FuelTypes.electricity)
    case VEHICLE_STATE.LOSS_KEY: // カギのトラブル
      // トラブル状況が「カギの紛失」選択時は自走できない場合の運び先候補表示対象
      return vehicleStateInfo.lossKeyStatus === LossKeyStatusTypes.loss
    case VEHICLE_STATE.OTHERS: // その他
      // トラブルの状況の選択が以下の場合は、自走できない場合の運び先候補表示対象
      // ・オイル漏れ
      // ・オーバーヒート
      // ・上記以外のトラブル
      return [
        OthersStatusTypes.oil,
        OthersStatusTypes.overheat,
        OthersStatusTypes.others,
      ].some((otherStatus) => otherStatus === vehicleStateInfo.othersStatus)
    default:
      break
  }
  return false
}

/**
 * 車高改造の有無が処理対象ならtrue, それ以外ならfalseを返却する
 * @param state QNState
 */
export const hasVehicleHeightModification = (state: QnStateProps): boolean => {
  return (
    isDestinationTarget(state) ||
    state.vehicleState.includes(VEHICLE_STATE.FLAT_TIRE)
  )
}

/** 確認表示用のデータ */
export type ConfirmItem = {
  /** ラベル */
  label: string
  /** 値 */
  values: Array<React.ReactNode | string>
}

/**
 * 単発入力欄の選択情報を生成
 * @param formState 項目の文言情報
 * @param value 項目の値
 */

export function createSingleItem(
  formState: VehicleStateFormItem,
  value: boolean | string
): ConfirmItem {
  return {
    label: formState.formName,
    values: [
      typeof value === 'boolean'
        ? value
          ? VEHICLE_HEIGHT_MODIFICATION_CONFIRM.YES
          : VEHICLE_HEIGHT_MODIFICATION_CONFIRM.NO
        : value,
    ],
  }
}

/**
 * 選択式入力欄の選択情報を生成
 * @param formState 項目の文言情報
 * @param value 項目の値
 */
export function createSelectItem<T extends string>(
  formState: VehicleStateSelectItem<T>,
  value: T | Array<T>
): ConfirmItem {
  return {
    label: formState.formName,
    values: Array.isArray(value)
      ? value.map((v) => formState.options[v])
      : [formState.options[value]],
  }
}

/**
 * チェック形式の選択ラベルのリストを取得
 * @param options 選択肢の文言情報
 * @param values 選択情報
 * @returns 選択されている選択肢のラベル一覧
 */
export function getCheckedLabels<T extends string>(
  options: OptionResource<T>,
  values: T[]
): Array<string> {
  return Object.entries<string>(options)
    .filter(([k]) => values.some((x) => x === k))
    .map(([, v]) => v)
}

/**
 * 画像のStateから必要な情報をpayloadに追加する
 * @param imageGroup 対象の画像State
 * @returns 要請者写真登録のpayload
 */
export const getImagePayload = (
  payload: ImagesAPIPayloadProps,
  imageGroup: AccidentImage
): void => {
  if (imageGroup.peripheral.image) {
    payload.surrounding = imageGroup.peripheral.image
  }
  if (imageGroup.damagedPart.image) {
    payload.damagedDetail = imageGroup.damagedPart.image
  }
  if (imageGroup.supplement.image) {
    payload.additional = imageGroup.supplement.image
  }
}

/**
 * 要請者入力情報連携APIのpayloadのtroubleDetailContentに情報を追記する
 * @param payload 編集対象のtroubleDetailContentを含むpayload
 * @param currentName 画面項目名
 * @param currentValues 項目内容
 */
export const addTroubleDetail = (
  payload: RequestAPIPayloadProps,
  currentName: string,
  currentValues: string | Array<string>
): void => {
  if (!currentName.length || !currentValues.length) {
    return
  }
  const currentValuesString = (Array.isArray(currentValues)
    ? currentValues
    : [currentValues]
  ).reduce(
    (accumulator, currentValue) =>
      (accumulator.length ? `${accumulator}、` : accumulator) + currentValue,
    ''
  )
  const currentContent = `${currentName}：${currentValuesString}`
  payload.troubleDetailContent =
    (payload.troubleDetailContent.length
      ? `${payload.troubleDetailContent}／`
      : payload.troubleDetailContent) + currentContent
}

/**
 * 要請者入力情報連携APIのpayloadのtroubleDetailContentにStateの中身を追記する
 * 対応しているGroupProps系の画面項目名、項目内容を自動で追記できる
 * @param payload 編集対象のtroubleDetailContentを含むpayload
 * @param selectedState 追加対象のState
 */
export const addTroubleDetailState = (
  payload: RequestAPIPayloadProps,
  selectedState:
    | SwitchGroupProps
    | RadioGroupProps
    | CheckGroupProps
    | TextAreaGroupProps
    | DivideCarFourGroupProps
): void => {
  const currentName = selectedState.formName
  const currentValues = ((): string | Array<string> => {
    if (isSwitchGroupProps(selectedState)) {
      return selectedState.switch.isSwitched ? selectedState.switch.label : ''
    } else if (isRadioGroupProps(selectedState)) {
      return getSelectedRadioLabel(selectedState)
    } else if (isCheckGroupProps(selectedState)) {
      return getSelectedCheckLabel(selectedState)
    } else if (isTextAreaGroupProps(selectedState)) {
      return selectedState.text.inputText
    } else if (isDivideCarFourGroupProps(selectedState)) {
      return getSelectedDamagedPartLabel(selectedState)
    }
    return ''
  })()
  addTroubleDetail(payload, currentName, currentValues)
}

export function addTroubleDetailBoolItem(
  payload: RequestAPIPayloadProps,
  formItem: VehicleStateSingleItem,
  value: boolean
): void {
  addTroubleDetail(payload, formItem.formName, value ? formItem.label : '')
}

/**
 * 選択式の入力情報をpayloadに追加する
 * @param payload APIのペイロード
 * @param formItem 入力欄の文言定義
 * @param value 選択された値
 * @param text フリー入力欄の値
 */
export function addTroubleDetailSelectItem<T extends string>(
  payload: RequestAPIPayloadProps,
  formItem: VehicleStateSelectItem<T>,
  value: T,
  text?: string
): void {
  const detail = [formItem.options[value], text].filter((v) => v).join(',')
  addTroubleDetail(payload, formItem.formName, detail)
}

/**
 * 周辺情報の補足内容が入力されていればpayloadに対象の項目を追加する
 * @param payload 編集対象のpayload
 * @param supplementState 周辺情報の補足内容のState
 */
export const addTroubleLocMark = (
  payload: RequestAPIPayloadProps,
  text: string
): void => {
  if (text) {
    // 目印／進行方向：補足内容
    payload.troubleLocMark = text
  }
}

/**
 * 車高改造系のpayloadを追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addVehicleHeightModificationPayload = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  if (hasVehicleHeightModification(state)) {
    const { vehicleStateInfo } = state
    // 改造：車高改造の有無
    if (vehicleStateInfo.isVehicleHeightModification) {
      // 改造：車高を低くしている
      payload.puncRemodelCd = '1'
      // 車高
      payload.vehicleHeight =
        VEHICLE_STATE_FORM.vehicleHeightStatus.options[
          vehicleStateInfo.vehicleHeightStatus
        ]
    }
    // トラブル詳細内容（追記）：車高改造の有無
    addTroubleDetailBoolItem(
      payload,
      VEHICLE_STATE_FORM.vehicleHeightModification,
      vehicleStateInfo.isVehicleHeightModification
    )
  }
}

/**
 * 搬送先系のpayloadを追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addDestinationPayload = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  const { destinationLocation, vehicleStateInfo } = state
  const { destinationType, crossPrefectures } = VEHICLE_STATE_FORM

  if (isDestinationTarget(state)) {
    // トラブル詳細内容（追記）：自走できない場合の運び先候補
    addTroubleDetail(
      payload,
      destinationType.formName,
      CLIENT_SETTINGS.options.destinationType[vehicleStateInfo.destinationType]
    )

    switch (vehicleStateInfo.destinationType) {
      // ご存知のディーラー・修理工場など予定
      case DestinationTypes.specify:
        // 希望搬送先
        payload.carryDestAddress = destinationLocation.address
        // 希望搬送先名
        payload.destinationName = destinationLocation.locationName
        break
      // 検討中
      case DestinationTypes.consider:
        if (vehicleStateInfo.crossPrefectures) {
          // 現境超えする
          payload.transportDestination = '2'
          payload.crossPrefBorder = '希望する'
          // トラブル詳細内容（追記）：都道府県を越えるか希望
          addTroubleDetail(
            payload,
            crossPrefectures.formName,
            crossPrefectures.label
          )
        } else {
          // しない
          payload.transportDestination = '1'
          payload.crossPrefBorder = '希望しない'
        }
        break
    }
  }
}

/**
 * payloadにパンク箇所を追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addPuncPlcNormCd = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  const { vehicleStateInfo } = state
  if (vehicleStateInfo.isMotorbike) {
    // バイク
    const keys = Object.keys(MotorbikePart)
    const selectedParts = Object.entries<string>(
      VEHICLE_STATE_FORM.flatTirePart.options
    ).filter(
      ([k]) =>
        keys.some((part) => part === k) &&
        vehicleStateInfo.accidentPart.parts.some((x) => x === k)
    )
    const code = vehicleStateInfo.accidentPart.parts.reduce((pre, current) => {
      switch (current) {
        case MotorbikePart.front:
          return pre + 1
        case MotorbikePart.rear:
          return pre + 2
      }
      return pre
    }, 0)
    payload.puncPlcTwoWheeledVehicleCd = `${code}`

    // 詳細追記
    const details = selectedParts.map(([, v]) => v)
    addTroubleDetail(payload, VEHICLE_STATE_FORM.flatTirePart.formName, details)
  } else {
    // 型定義の順番で選択されている部位を取得
    const keys = Object.keys(FourWheeledVehiclePart)
    const selectedParts = Object.entries<string>(
      VEHICLE_STATE_FORM.flatTirePart.options
    ).filter(
      ([k]) =>
        keys.some((part) => part === k) &&
        vehicleStateInfo.accidentPart.parts.some((x) => x === k)
    )

    // コード値に変換
    const damagedParts = selectedParts
      .map(([k]) => {
        switch (k) {
          case FourWheeledVehiclePart.leftFront:
            return '1'
          case FourWheeledVehiclePart.rightFront:
            return '2'
          case FourWheeledVehiclePart.leftRear:
            return '3'
          case FourWheeledVehiclePart.rightRear:
          default:
            return '4'
        }
      })
      .join(',')
    payload.puncPlcNormCd = damagedParts

    // 追記
    addTroubleDetail(
      payload,
      VEHICLE_STATE_FORM.flatTirePart.formName,
      selectedParts.map(([, v]) => v)
    )
  }
}

/**
 * payloadに破損箇所を追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export function addCollAccPlcCd(
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void {
  const { vehicleStateInfo } = state
  if (!vehicleStateInfo.noPhoto) {
    // 写真以外で指定が選択されていない場合は処理しない
    return
  }

  // タイヤの破損状況
  payload.collAccPlcTireCd = vehicleStateInfo.accidentPart.existsDamagedTire
    ? '2'
    : '1'
  const tireStatus = vehicleStateInfo.accidentPart.existsDamagedTire
    ? [VEHICLE_STATE_FORM.accidentPart.label]
    : []

  if (vehicleStateInfo.isMotorbike) {
    // バイク
    const keys = Object.keys(MotorbikePart)
    const selectedParts = Object.entries<string>(
      VEHICLE_STATE_FORM.accidentPart.options
    ).filter(
      ([k]) =>
        keys.some((part) => part === k) &&
        vehicleStateInfo.accidentPart.parts.some((x) => x === k)
    )
    const code = vehicleStateInfo.accidentPart.parts.reduce((pre, current) => {
      switch (current) {
        case MotorbikePart.front:
          return pre + 1
        case MotorbikePart.rear:
          return pre + 2
      }
      return pre
    }, 0)
    payload.puncPlcTwoWheeledVehicleCd = `${code}`

    // 追記
    const details = selectedParts.map(([, v]) => v)
    addTroubleDetail(
      payload,
      VEHICLE_STATE_FORM.accidentPart.formName,
      details.concat(tireStatus)
    )
  } else {
    // 型定義の順番で選択されている部位を取得
    const keys = Object.keys(FourWheeledVehiclePart).concat(
      Object.keys(AllPart)
    )
    const selectedParts = Object.entries<string>(
      VEHICLE_STATE_FORM.accidentPart.options
    ).filter(
      ([k]) =>
        keys.some((part) => part === k) &&
        vehicleStateInfo.accidentPart.parts.some((x) => x === k)
    )
    // 前面
    const allFront = selectedParts.some(([k]) => k.includes(AllPart.allFront))
      ? '2'
      : undefined
    // 後面
    const allRear = selectedParts.some(([k]) => k.includes(AllPart.allRear))
      ? '8'
      : undefined
    // 部位をコード値の配列に変換
    const damagedParts = selectedParts.reduce((pre, [k]) => {
      if (!allFront) {
        // 前面指定がなければ前の部位を追加
        switch (k) {
          case FourWheeledVehiclePart.leftFront:
            return pre.concat('1')
          case FourWheeledVehiclePart.rightFront:
            return pre.concat('3')
        }
      }
      if (!allRear) {
        // 後面指定がなければ後の部位を追加
        switch (k) {
          case FourWheeledVehiclePart.leftRear:
            return pre.concat('7')
          case FourWheeledVehiclePart.rightRear:
            return pre.concat('9')
        }
      }
      // 追加対象以外は前の値をそのまま返す
      return pre
    }, [] as string[])
    // コードをマージして','で区切った文字列に変換
    payload.collAccPlcCd = [allFront, ...damagedParts, allRear]
      .filter((x) => x)
      .join(',')

    // 追記
    addTroubleDetail(
      payload,
      VEHICLE_STATE_FORM.accidentPart.formName,
      selectedParts.map(([, v]) => v).concat(tireStatus)
    )
  }
}

/**
 * payloadに落輪・乗り上げ箇所を追加する
 * @param payload 編集対象のpayload
 * @param formItem 入力欄の文言データ
 * @param state QNState
 */
export function addVehiclePartCd<T extends string>(
  payload: RequestAPIPayloadProps,
  formItem: VehicleStatePartItem<T>,
  state: QnStateProps
): void {
  const { vehicleStateInfo } = state
  if (!vehicleStateInfo.noPhoto) {
    // 写真以外で指定が選択されていない場合は処理しない
    return
  }

  // タイヤの破損状況
  payload.collAccPlcTireCd = vehicleStateInfo.accidentPart.existsDamagedTire
    ? '2'
    : '1'
  const tireStatus = vehicleStateInfo.accidentPart.existsDamagedTire
    ? [VEHICLE_STATE_FORM.accidentPart.label]
    : []

  if (vehicleStateInfo.isMotorbike) {
    // バイク
    const keys = Object.keys(MotorbikePart)
    const selectedParts = Object.entries<string>(formItem.options).filter(
      ([k]) =>
        keys.some((part) => part === k) &&
        vehicleStateInfo.accidentPart.parts.some((x) => x === k)
    )
    const code = vehicleStateInfo.accidentPart.parts.reduce((pre, current) => {
      switch (current) {
        case MotorbikePart.front:
          return pre + 1
        case MotorbikePart.rear:
          return pre + 2
      }
      return pre
    }, 0)
    payload.puncPlcTwoWheeledVehicleCd = `${code}`

    // 追記
    const details = selectedParts.map(([, v]) => v)
    addTroubleDetail(payload, formItem.formName, details.concat(tireStatus))
  } else {
    // 型定義の順番で選択されている部位を取得
    const keys = Object.keys(FourWheeledVehiclePart)
    const selectedParts = Object.entries<string>(formItem.options).filter(
      ([k]) =>
        keys.some((part) => part === k) &&
        vehicleStateInfo.accidentPart.parts.some((x) => x === k)
    )

    // コード値に変換
    const damagedParts = selectedParts
      .map(([k]) => {
        switch (k) {
          case FourWheeledVehiclePart.leftFront:
            return '1'
          case FourWheeledVehiclePart.rightFront:
            return '2'
          case FourWheeledVehiclePart.leftRear:
            return '3'
          case FourWheeledVehiclePart.rightRear:
          default:
            return '4'
        }
      })
      .join(',')
    if (
      vehicleStateInfo.fallingWheelStatus.includes(
        FallingWheelStatusTypes.falling
      )
    ) {
      payload.fallingPartNormaMultipleCd = damagedParts
    } else {
      payload.ridingPointNormalMultipleCd = damagedParts
    }

    // 追記
    addTroubleDetail(
      payload,
      formItem.formName,
      selectedParts.map(([, v]) => v).concat(tireStatus)
    )
  }
}

/**
 * payloadに落輪した場所を追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addDropLocationCd = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  const { vehicleStateInfo } = state
  if (
    vehicleStateInfo.noPhoto &&
    vehicleStateInfo.fallingWheelStatus.includes(
      FallingWheelStatusTypes.falling
    )
  ) {
    // 写真以外で状況説明 && 落輪
    payload.dropLocationCd = {
      // 落輪した場所：側溝
      [FieldTypes.gutter]: '1',
      // 落輪した場所：縁石・車留め
      [FieldTypes.curb]: '99',
      // 落輪した場所：その他（例：花壇・看板等）
      [FieldTypes.others]: '3',
    }[vehicleStateInfo.fieldType.type]
  }
}

/**
 * payloadに乗り上げた場所を追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addRideLocationCd = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  const { vehicleStateInfo } = state
  if (
    vehicleStateInfo.noPhoto &&
    vehicleStateInfo.fallingWheelStatus.includes(FallingWheelStatusTypes.rideup)
  ) {
    // 写真以外で状況説明 && 落輪
    payload.rideLocationCd = {
      // 乗り上げた場所：側溝
      [FieldTypes.gutter]: '99',
      // 乗り上げた場所：縁石・車留め
      [FieldTypes.curb]: '2',
      // 乗り上げた場所：その他（例：花壇・看板等）
      [FieldTypes.others]: '99',
    }[vehicleStateInfo.fieldType.type]
  }
}

/**
 * 事故現場をpayloadのtroubleDetailContentに追記する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addFieldTypeTroubleDetail = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  const { vehicleStateInfo } = state
  const selectedFieldType: Array<string> = [
    VEHICLE_STATE_FORM.fieldType.options[vehicleStateInfo.fieldType.type],
  ]
  if (
    vehicleStateInfo.fieldType.type.includes(FieldTypes.others) &&
    vehicleStateInfo.fieldType.others
  ) {
    selectedFieldType.push(vehicleStateInfo.fieldType.others)
  }

  // トラブル詳細内容（追記）：現場の種類
  addTroubleDetail(
    payload,
    VEHICLE_STATE_FORM.fieldType.formName,
    selectedFieldType
  )
}

/**
 * 送信する住所をフォーマットする
 * @param string フォーマット前の住所
 */
export const formatSendAddress = (address: string): string => {
  // PrimeLINEでは郵便番号が無いほうが検索の仕組み上都合が良いため、郵便番号を削除する
  return address.replace(/〒\d{3}-\d{4}/g, '').trim()
}

/**
 * 特約利用選択のpayloadを追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addSpecialContractPayload = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  if (hasSpecialContract(state)) {
    // 宿泊・移動費用特約
    if (hasRaLodgingTransfer(state)) {
      // 移動費用特約利用
      if (
        isSelectedCheck(state.raTransferScWishes, RA_TRANSFER_SC_WISHES.YES)
      ) {
        // 移動費用特約利用：公共交通機関・タクシーを利用する
        payload.raTransferScWishes = '1'
      }
      if (isSelectedCheck(state.raTransferScWishes, RA_TRANSFER_SC_WISHES.NO)) {
        // 移動費用特約利用：徒歩または迎えがくるので利用しない
        payload.raTransferScWishes = '2'
      }
      if (
        isSelectedCheck(
          state.raTransferScWishes,
          RA_TRANSFER_SC_WISHES.THINKING
        )
      ) {
        // 移動費用特約利用：検討中
        payload.raTransferScWishes = '3'
      }

      // 宿泊費用特約利用
      if (isSelectedCheck(state.raLodgingScWishes, RA_LODGING_SC_WISHES.YES)) {
        // 宿泊費用特約利用：宿泊施設を利用する
        payload.raLodgingScWishes = '1'
      }
      if (isSelectedCheck(state.raLodgingScWishes, RA_LODGING_SC_WISHES.NO)) {
        // 宿泊費用特約利用：宿泊施設を利用しない
        payload.raLodgingScWishes = '2'
      }
      if (
        isSelectedCheck(state.raLodgingScWishes, RA_LODGING_SC_WISHES.THINKING)
      ) {
        // 宿泊費用特約利用：検討中
        payload.raLodgingScWishes = '3'
      }
    }

    // 代車費用特約
    if (hasRaLcarCost(state)) {
      // 代車費用特約利用
      if (
        isSelectedCheck(state.raLcarCostScWishes, RA_LCAR_COST_SC_WISHES.YES)
      ) {
        // 代車費用特約利用：レンタカーの手配を希望・相談する
        payload.raLcarCostScWishes = '1'
      }
      if (
        isSelectedCheck(state.raLcarCostScWishes, RA_LCAR_COST_SC_WISHES.NO)
      ) {
        // 代車費用特約利用：レンタカーを利用しない
        payload.raLcarCostScWishes = '2'
      }
      if (
        isSelectedCheck(
          state.raLcarCostScWishes,
          RA_LCAR_COST_SC_WISHES.ALREADY_ARRANGED
        )
      ) {
        // 代車費用特約利用：レンタカーは自身または修理先で手配済（予定）
        payload.raLcarCostScWishes = '3'
      }

      if (state.raLcarCostScSupplWishes.text.inputText) {
        // 代車補足内容：補足内容
        // PL側で改行とタブ文字が禁止のため削除
        payload.raLcarCostScSupplWishes = deleteReturnTabString(
          state.raLcarCostScSupplWishes.text.inputText
        )
      }
    }
  }
}

/**
 * 予約情報のpayloadを追加する
 * @param payload 編集対象のpayload
 * @param state QNState
 */
export const addReservationPayload = (
  payload: RequestAPIPayloadProps,
  state: QnStateProps
): void => {
  if (state.reservation.date && state.reservation.time) {
    // 予約日：MM/DD
    const selectedDate = dayjs(state.reservation.date)
    payload.reservationDate = selectedDate.format(RESERVATION_DATE_API_FORMAT)
    // 予約時間
    const startTime = dayjs(`2000/01/01 ${state.reservation.time}`)
    const endTime = startTime.add(2, 'h')
    payload.reservationTime = `${startTime.format('HH:mm')}～${endTime.format(
      'HH:mm'
    )}`
  }
}
